
import api from '@/api/my/create';
import { HOST_FEATURES } from '@/const';
import { HostType } from '@/types/enums';
import utils from '@/utils';
import { defineComponent } from 'vue';
import QRCode from 'qrcode';

interface IHostFeature {
  functions: string;
  starter: boolean;
  standard: boolean;
  professional: boolean;
}

interface IBuyForm {
  hostType: string;
  year: number;
  isHongKong: boolean;
  agreement: boolean;
}

interface IPayForm {
  hostType: string;
  year: number;
  isHongKong: boolean;
  channel: string;
  isPurchased: boolean;
  isQrCode: boolean;
  orderNo: string;
  failureCount: 0;
}

interface IState {
  active: number;
  themeUserName: string;
  themeName: string;
  hostFeatures: IHostFeature[];
  themePreviewUrl: string;
  themePrice: number;
  themeDiscountStarter: number;
  themeDiscountStandard: number;
  themeDiscountProfessional: number;
  priceStarter: number;
  priceStandard: number;
  priceProfessional: number;
  discount1Year: number;
  discount2Year: number;
  discount3Year: number;
  discount4Year: number;
  discount5Year: number;
  buyForm: IBuyForm;
  payForm: IPayForm;
}

export default defineComponent({
  data() {
    return {
      active: 0,
      themeUserName: '',
      themeName: '',
      hostFeatures: HOST_FEATURES,
      themePreviewUrl: '',
      themePrice: 0,
      themeDiscountStarter: 0,
      themeDiscountStandard: 0,
      themeDiscountProfessional: 0,
      priceStarter: 0,
      priceStandard: 0,
      priceProfessional: 0,
      discount1Year: 0,
      discount2Year: 0,
      discount3Year: 0,
      discount4Year: 0,
      discount5Year: 0,
      buyForm: {} as IBuyForm,
      payForm: {} as IPayForm,
    } as IState;
  },
  methods: {
    apiGet() {
      this.active = 0;
      this.themeUserName = '';
      this.themeName = '';
      const resource = this.$route?.params?.resource as string;
      if (resource && resource.indexOf('-') !== -1) {
        [this.themeUserName, this.themeName] = resource.split('-');
      }

      api.get(
        {
          themeUserName: this.themeUserName,
          themeName: this.themeName,
        },
        (res) => {
          this.themePreviewUrl = res.themePreviewUrl;
          this.themePrice = res.themePrice;
          this.themeDiscountStarter = res.themeDiscountStarter;
          this.themeDiscountStandard = res.themeDiscountStandard;
          this.themeDiscountProfessional = res.themeDiscountProfessional;
          this.priceStarter = res.priceStarter;
          this.priceStandard = res.priceStandard;
          this.priceProfessional = res.priceProfessional;
          this.discount1Year = res.discount1Year;
          this.discount2Year = res.discount2Year;
          this.discount3Year = res.discount3Year;
          this.discount4Year = res.discount4Year;
          this.discount5Year = res.discount5Year;
        },
      );
    },

    btnBuyClick(type: string) {
      this.buyForm.hostType = type;
      this.buyForm.year = 1;
      this.buyForm.isHongKong = false;
      this.buyForm.agreement = false;
      this.active = 1;
    },

    getChannel() {
      return this.payForm.channel === 'AliPay' ? '支付宝' : '微信';
    },

    getPayDescription() {
      const hostType = utils.getHostType(this.buyForm.hostType);
      const amount = this.getPayAmount('all').toFixed(2);
      let themeDesc = '';
      if (this.themePrice > 0) {
        themeDesc = `
        模板名称:
        <span style="color: #fa5300; margin-right: 15px">${this.themeName}</span>`;
      }
      return `
        网站云版本:
        <span style="color: #fa5300; margin-right: 15px">${hostType}${this.payForm.isHongKong ? '(香港)' : ''}</span>
        购买时长:
        <span style="color: #fa5300; margin-right: 15px">${this.payForm.year}年</span>
        ${themeDesc}
        费用合计:
        <span style="color: #fa5300">￥${amount}</span>`;
    },

    payQuery() {
      const interval = setInterval(() => {
        api.query(
          {
            orderNo: this.payForm.orderNo,
          },
          (res) => {
            this.payForm.isPurchased = res.isPurchased;

            if (this.payForm.isPurchased) {
              clearInterval(interval);
            }
          },
        );
      }, 3000);
    },

    btnPayChannelClick(channel: string) {
      this.payForm.channel = channel;

      api.pay(
        {
          hostType: this.payForm.hostType,
          year: this.payForm.year,
          isHongKong: this.payForm.isHongKong,
          themeUserName: this.themeUserName,
          themeName: this.themeName,
          channel: this.payForm.channel,
          amount: this.getPayAmount('all'),
        },
        (res) => {
          if (!res) return;

          this.payForm.isQrCode = true;
          this.payForm.orderNo = res.orderNo;

          setTimeout(() => {
            QRCode.toCanvas(document.getElementById('qrcode'), res.qrCodeUrl, { width: 260 });
          }, 100);

          this.payQuery();
        },
        (error) => {
          console.log(error);
          this.payForm.failureCount += 1;
          if (this.payForm.failureCount < 3) {
            this.btnPayChannelClick(channel);
          } else {
            utils.loading(false);
            utils.error(error);
          }
        },
      );
    },

    getThemeDiscount(): string {
      let discount = 1;
      const { hostType } = this.buyForm;
      if (hostType === HostType.Starter) {
        discount = this.themeDiscountStarter;
      } else if (hostType === HostType.Standard) {
        discount = this.themeDiscountStandard;
      } else if (hostType === HostType.Professional) {
        discount = this.themeDiscountProfessional;
      }
      return discount < 1 ? `（${discount * 10}折）` : '';
    },

    getPayAmount(contains: 'all' | 'host' | 'theme'): number {
      const { hostType } = this.buyForm;
      const { year } = this.buyForm;

      let price = 0;
      let themeDiscount = 1;
      if (hostType === HostType.Starter) {
        price = this.priceStarter;
        themeDiscount = this.themeDiscountStarter;
      } else if (hostType === HostType.Standard) {
        price = this.priceStandard;
        themeDiscount = this.themeDiscountStandard;
      } else if (hostType === HostType.Professional) {
        price = this.priceProfessional;
        themeDiscount = this.themeDiscountProfessional;
      }
      let discount = 1;
      if (year === 1) {
        discount = this.discount1Year;
      } else if (year === 2) {
        discount = this.discount2Year;
      } else if (year === 3) {
        discount = this.discount3Year;
      } else if (year === 4) {
        discount = this.discount4Year;
      } else if (year === 5) {
        discount = this.discount5Year;
      }

      const hostAmount = price * year * discount;
      const themeAmount = this.themePrice * themeDiscount;
      if (contains === 'host') {
        return hostAmount;
      }
      if (contains === 'theme') {
        return themeAmount;
      }
      return hostAmount + themeAmount;
    },

    getPaySuccessMessage() {
      if (this.themePrice > 0) {
        return '网站云及模板购买成功，请进入我的网站云安装并使用！';
      }
      return '网站云购买成功，请进入我的网站云安装并使用！';
    },

    btnPayCloseClick() {
      utils.pageClose(this.$route.fullPath, () => {
        utils.pageRedirect('/my/hosts', { r: Math.random() });
      });
    },

    btnPreviousClick() {
      this.active -= 1;
      if (this.active < 0) this.active = 0;
    },

    btnNextClick() {
      this.active += 1;
      if (this.active === 2) {
        this.payForm.hostType = this.buyForm.hostType;
        this.payForm.year = this.buyForm.year;
        this.payForm.isHongKong = this.buyForm.isHongKong;
        this.payForm.failureCount = 0;
      }
    },
  },
  created() {
    this.$watch(
      () => this.$route.params,
      () => {
        this.apiGet();
      },
      { immediate: true },
    );
  },
});
